import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

type NavigateBackWithParamsOptions = {
  /**
   * Search params to set on the previous page after navigating back.
   */
  searchParams?: Record<string, string>;
};

/**
 * Hook for navigating back while preserving/setting search params.
 *
 * Uses browser history (navigate(-1)) then merges the provided params into the URL.
 * Existing search params from the previous page are preserved.
 *
 * @example
 * const { navigateBackWithParams } = useNavigateWithHistory();
 *
 * // Navigate back and ensure sessionId is set (preserves other params like searchQueryId)
 * navigateBackWithParams({ searchParams: { sessionId: 'abc' } });
 */
export const useNavigateWithHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate back in browser history, then merge the given search params into the URL.
   * This preserves the browser's back/forward behavior while ensuring specific params are set.
   * Existing search params from the previous page are preserved.
   */
  const navigateBackWithParams = useCallback(
    (options?: NavigateBackWithParamsOptions) => {
      const { searchParams } = options ?? {};

      navigate(-1);

      if (searchParams && Object.keys(searchParams).length > 0) {
        setTimeout(() => {
          // Merge new params into existing URL params (preserves searchQueryId, filters, etc.)
          const currentUrl = new URL(window.location.href);
          Object.entries(searchParams).forEach(([key, value]) => {
            currentUrl.searchParams.set(key, value);
          });
          navigate(currentUrl.pathname + currentUrl.search, {
            replace: true
          });
        }, 50);
      }
    },
    [navigate]
  );

  /**
   * Get the current path (pathname + search) for manual use.
   */
  const getCurrentPath = useCallback(
    () => location.pathname + location.search,
    [location.pathname, location.search]
  );

  return {
    navigateBackWithParams,
    getCurrentPath
  };
};
